/* Imports needed for all question form subclasses. */
import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import * as _ from 'lodash';
import {
    QuestionFormBase,
    QuestionFormBaseProps,
    QuestionFormBaseState,
    mapStateToQuestionFormBaseProps,
    mapDispatchToQuestionFormBaseProps
} from '../../QuestionFormBase';
import NotifyHOC from '../../../../../../HOC/Notifications';
import {
    QTYPES,
    QuestionSummary,
    PROJECT_EDIT_STATUS
} from '@nexxt/common/types';

/* Components used in this question form. */
import InputRadio from '../../../common/radioButton/inputRadio';
import BuilderHeader from '../../../common/builderHeader';
import CheckboxMultiSelectPipeOption from '../../../../../common/reactSelect/CheckboxMultiSelectPipeOption';
import {
    getChildQuestionFromSortableQuestion,
    getAvailableChildrenQuestions,
    getUpdatedFlow
} from './group';
import Notes from '../../../../../common/notes/Notes';
import ShowQuestion from './ShowQuestion';
import { parseQuestionSummaryToObjects } from '../../../../../../utils/question';
import { ALERT_MESSAGE_TYPES } from '../../../../../../interface/project.interface';
import AutoSavingService from '../../../../../../utils/AutoSavingService';

type Props = QuestionFormBaseProps;

interface States extends QuestionFormBaseState {
    readonly groupQuestionOptions: QuestionSummary[];
    readonly rSelectedQuestions: QuestionSummary[];
    selectedQuestions: QuestionSummary[];
    sortableQuestionObject: { [id: number]: QuestionSummary };
}

class GroupQuestion extends QuestionFormBase<Props, States> {
    constructor(p: QuestionFormBaseProps) {
        super(p, QTYPES.GROUP);
        // set value from question here
        /** being used sortalbeQuestionObject here.  @todo not to use sortableQuestionObject */
        const { currentQuestionId, questionSummaries } = this.props;
        let selectedQuestions: QuestionSummary[] = [];
        const sortableQuestionObject =
            parseQuestionSummaryToObjects(questionSummaries);
        if (this.state.question.id) {
            selectedQuestions = getChildQuestionFromSortableQuestion(
                currentQuestionId,
                sortableQuestionObject
            );
        }
        const question = {
            ...this.state.question,
            pickNumber: this.state.question.pickNumber
                ? this.state.question.pickNumber
                : 0,
            groupType:
                this.state.question.groupType === null
                    ? 'sequential'
                    : this.state.question.groupType
        };
        this.state = {
            ...this.state,
            question,
            selectedQuestions,
            rSelectedQuestions: selectedQuestions,
            groupQuestionOptions:
                getAvailableChildrenQuestions(
                    currentQuestionId,
                    sortableQuestionObject,
                    questionSummaries || []
                ) || [],
            sortableQuestionObject,
            autoSavingService: new AutoSavingService(
                { question, selectedQuestions },
                `question|${this.props.currentQuestionId || 'new'}`,
                false,
                this.props.editStatus,
                this.props.setEditedStatus,
                this.props.clearEditedStatus
            )
        };
    }

    componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<States>
    ): void {
        if (
            !_.isEqual(prevState.question, this.state.question) ||
            !_.isEqual(
                prevState.selectedQuestions,
                this.state.selectedQuestions
            )
        ) {
            this.state.autoSavingService.setData({
                question: this.state.question,
                selectedQuestions: this.state.selectedQuestions
            });
        }
        if (!_.isEqual(prevProps.editStatus, this.props.editStatus)) {
            this.state.autoSavingService.setCurrentEditStatus(
                this.props.editStatus
            );
        }

        if (prevState.isSaving !== this.state.isSaving) {
            this.state.autoSavingService.setIsSaving(this.state.isSaving);
        }
    }

    async componentDidMount(): Promise<void> {
        console.log('question did mount');
        if (this.props.isNewQuestion) {
            await this.onSave();
        }
        this.getTemplateSettings();
        this.state.autoSavingService.setAutoSavingInterval(this.onSave);
        this.setEditFlag(true);
    }

    setEditFlag = (init = false) => {
        const { autoSavingService } = this.state;
        !init && autoSavingService.setEditing();
        const checkValidity =
            this.getValidationService().validateByQuestionType(
                this.state.question,
                this.props.currentProject.languages,
                this.state,
                this.props.questionTitles
            );

        this.setState({
            questionError: checkValidity.isError
                ? checkValidity.errorObject
                : {}
        });
        autoSavingService.setEditFlag(checkValidity, {
            editedExtraCondition: !_.isEqual(
                this.state.selectedQuestions,
                this.state.rSelectedQuestions
            )
        });
    };

    /**
     * Update selected question children
     */
    selectChildQuestion = (keyName: string, value: any) => {
        if (!this.state.isSaving) {
            this.setState(
                {
                    ...this.state,
                    [keyName]: value
                },
                () => {
                    if (
                        !_.isEqual(
                            this.state.rSelectedQuestions,
                            this.state.selectedQuestions
                        )
                    ) {
                        this.setEditFlag();
                    }
                }
            );
        }
    };

    /**
     * @override onSave function from QuestionFormBase
     * Compare selectedQuestions with rSelectedQuestions to get updated flow
     */
    onSave = async () => {
        if (this.state.isSaving) {
            return;
        }
        const { selectedQuestions, rSelectedQuestions } = this.state;
        const checkValidity =
            this.getValidationService().validateByQuestionType(
                this.state.question,
                this.props.currentProject.languages,
                this.props.questionTitles
            );
        // if (checkValididity.isError) {
        //     this.setState({
        //         showError: true,
        //         questionError: checkValididity.errorObject
        //     });
        //     if (this.props.viewMode !== VIEW_MODE.DEV) {
        //         this.props.toastNotification.error(
        //             alertMessage.QUESTION_FORM_INVALID
        //         );
        //         return;
        //     } else {
        //         const forceProceed = await getConfirmationBox({
        //             message: alertMessage.QUESTION_FORM_INVALID_CONTINUE,
        //             showCloseButton: true,
        //             confirmButtonColor: '',
        //             title: 'Save a question'
        //         }).then((x) => {
        //             if (!x.isConfirmed) return false;
        //             return true;
        //         });

        //         if (!forceProceed) return;
        //     }
        // }
        this.setState({
            showError: true,
            questionError: checkValidity.isError
                ? checkValidity.errorObject
                : {},
            isSaving: true
        });
        try {
            let data;
            if (this.props.currentQuestionId) {
                data = await this.props.updateQuestion(this.state.question);
            } else {
                data = await this.props.createQuestion(
                    this.state.question,
                    this.props.currentProject.id
                );
                this.setState({ question: data });
            }
            if (!_.isEqual(selectedQuestions, rSelectedQuestions)) {
                const flow = getUpdatedFlow(
                    data.id,
                    selectedQuestions,
                    this.props.questionSummaries,
                    this.state.sortableQuestionObject
                );
                await this.props.saveUpdatedSortedQuestion(
                    this.props.currentProject.id,
                    flow
                );
            }
            this.state.autoSavingService.setSyncedData({
                question: data,
                selectedQuestions
            });
            this.setState({
                isSaving: false,
                rSelectedQuestions: selectedQuestions
            });
            this.props.clearEditedStatus(
                checkValidity.isError
                    ? PROJECT_EDIT_STATUS.EDIT_REQUIRED
                    : PROJECT_EDIT_STATUS.COMPLETED
            );
        } catch (e) {
            this.setState({
                isSaving: false
            });
            this.props.clearEditedStatus(PROJECT_EDIT_STATUS.FAILED);
        }
    };

    render() {
        const { question } = this.state;
        const questionError =
            (this.state.showError && this.state.questionError) || {};
        return (
            <div>
                <div className="project-add--question-builder">
                    <BuilderHeader
                        toggleSettingBoxLayout={() => {}}
                        isSettingBoxLayoutOpen={false}
                        showQuestionSetting={false}
                        showAlert={!this.props.editEnabled}
                        alertMessages={this.getWarningMessage()}
                        question={question}
                        setValue={this.setQuestion}
                        disabled={!this.props.editEnabled}
                        errorObject={this.state.questionError}
                    />
                    <div className="builder-body">
                        {this.renderErrorNotes()}
                        <div className="option-settings-block">
                            <div className="form-group-header">
                                Select question that fall into this group
                            </div>
                            <Notes note="You need to create individual questions separately before adding them to the group." />
                            {this.state.question.id ? (
                                <div
                                    className={`form-group ${
                                        false ? 'error' : ''
                                    }`}
                                >
                                    <CheckboxMultiSelectPipeOption
                                        options={
                                            this.state.groupQuestionOptions
                                        }
                                        value={this.state.selectedQuestions}
                                        updateLabel={'selectedQuestions'}
                                        onChangeCallback={
                                            this.selectChildQuestion
                                        }
                                        disabled={!this.props.editEnabled}
                                    />
                                </div>
                            ) : (
                                <Notes
                                    type={ALERT_MESSAGE_TYPES.WARN}
                                    note="You need to save this question first to add other questions to the group."
                                />
                            )}
                        </div>
                        <ShowQuestion
                            questionSummaries={this.state.selectedQuestions}
                            languages={this.props.currentProject.languages}
                        />
                        <div className="row">
                            <div
                                className={classnames('col-md-12 form-group', {
                                    error:
                                        questionError && questionError.groupType
                                })}
                            >
                                <label className="form-group__label form-group__label--block">
                                    Question Display Options
                                </label>
                                <InputRadio
                                    options={[
                                        {
                                            label: 'Show all in order',
                                            value: 'sequential'
                                        },
                                        {
                                            label: 'Randomize all and randomly pick some',
                                            value: 'picksomefromgroup'
                                        },
                                        {
                                            label: 'Randomize all',
                                            value: 'randomized'
                                        }
                                    ]}
                                    name="groupType"
                                    selectedValue={question.groupType}
                                    setValue={(value) =>
                                        this.setQuestion('groupType', value)
                                    }
                                    disabled={!this.props.editEnabled}
                                />
                            </div>
                        </div>
                        {question.groupType === 'picksomefromgroup' && (
                            <div className="row">
                                <div
                                    className={classnames(
                                        'col-md-6 form-group',
                                        {
                                            error:
                                                questionError &&
                                                questionError.pickNumber
                                        }
                                    )}
                                >
                                    <label className="form-group__label form-group__label--block">
                                        Number of randomly selected questions
                                    </label>
                                    <input
                                        className="form-group__control"
                                        placeholder="0"
                                        type="number"
                                        value={question.pickNumber}
                                        name="pickNumber"
                                        onChange={(event) =>
                                            this.setQuestion(
                                                'pickNumber',
                                                event.target.value
                                            )
                                        }
                                        min="1"
                                        disabled={
                                            !this.props.currentQuestionId ||
                                            !this.props.editEnabled
                                        }
                                        max={
                                            this.state.selectedQuestions.length
                                        }
                                    />
                                    {questionError &&
                                    questionError.pickNumber ? (
                                        <span className="form-group__error-msg">
                                            {questionError.pickNumber}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default NotifyHOC(
    connect(
        mapStateToQuestionFormBaseProps,
        mapDispatchToQuestionFormBaseProps
    )(GroupQuestion)
);
